import { Play } from 'lucide-react';
import { STEP_ACTION_ICONS } from '@/components/processes/step-action-icons';
import {
  getBpmnEventIcon,
  Item,
  ItemMedia,
  ItemContent,
  ItemTitle,
  ItemDescription,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  type BpmnEventType
} from '@bliks-io/ui';
import type { ProcessStepAssignment } from '@/api/queries/useTasks';
import type { ProcessStepInfo } from '../types';
import type { ProcessStepResult } from '../utils/processStepInfo';
import type { AssignmentProcess } from '@bliks-io/contracts';

interface ProcessStepCellProps {
  step: ProcessStepAssignment['sourceProcessStep'] | ProcessStepInfo;
  getStepInfo: (
    step: ProcessStepAssignment['sourceProcessStep'] | ProcessStepInfo,
    ruleIds?: string[],
    assignment?: ProcessStepAssignment | { process: string | AssignmentProcess }
  ) => ProcessStepResult;
  ruleIds?: string[];
  assignment?: ProcessStepAssignment | { process: string | AssignmentProcess };
}

/**
 * Cell component for displaying process step information in tables
 * Shows icon, label, and description based on action type
 */
export function ProcessStepCell({ step, getStepInfo, ruleIds, assignment }: ProcessStepCellProps) {
  const stepInfo = getStepInfo(step, ruleIds, assignment);

  // Determine icon based on action type
  let Icon = null;
  if (stepInfo.actionType === 'task' || stepInfo.actionType === 'decision') {
    Icon = STEP_ACTION_ICONS[stepInfo.actionType];
  } else if (stepInfo.actionType === 'start') {
    // Start-event icon: the populated event node serves its type directly
    // (issue 11). Legacy 'START' rows (migration M2's orphan-repair stock,
    // kept one release past P6) degrade to the generic Play icon — the
    // initialEvent mirror they used to read is gone.
    const eventType = stepInfo.eventType;
    if (eventType && eventType !== 'none') {
      Icon = getBpmnEventIcon(eventType as BpmnEventType);
    } else {
      Icon = Play;
    }
  }

  const details = stepInfo.ruleConditions?.length ? (
    <>
      {stepInfo.ruleConditions.map(({ key, value }) => (
        <div key={key} className="text-xs">
          <span className="font-medium">{key}:</span> {value}
        </div>
      ))}
      {stepInfo.ruleOutput && (
        <div className="text-xs mt-1 pt-1 border-t border-border">
          <span className="font-medium">→</span> {stepInfo.ruleOutput}
        </div>
      )}
    </>
  ) : (
    stepInfo.description
  );

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <div className="max-w-sm overflow-hidden">
          <Item variant="default" size="sm" className="w-full">
            {Icon && (
              <ItemMedia variant="icon" className="shrink-0">
                <Icon className="size-4" />
              </ItemMedia>
            )}
            <ItemContent className="min-w-0 overflow-hidden">
              <ItemTitle className="truncate">{stepInfo.label}</ItemTitle>
              {details && <ItemDescription className="space-y-0.5">{details}</ItemDescription>}
            </ItemContent>
          </Item>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-md">
        <div className="space-y-0.5 break-words">
          <div className="font-medium">{stepInfo.label}</div>
          {details && <div className="space-y-0.5 text-xs">{details}</div>}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
